import React, {
  ClipboardEvent,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import HelpTooltip from '~/components/HelpTooltip';

import {
  buildSegmentNodes,
  createChipElement,
  ensureCaretSlots,
  handlePlainArrowAroundChipBoundary,
  isChipElement,
  isInsideChip,
  renderInto,
  selectChipForNativeDeletion,
  serialize,
  stripCaretMarkers,
} from './tokenDom';
import {
  ChipTooltip,
  DropdownCard,
  DropdownOption,
  DropdownOptionHelper,
  DropdownOptionName,
  Editor,
  EditorAdornmentSlot,
  EditorBox,
  EditorRow,
  ErrorText,
  FieldContainer,
  FieldLabel,
  FieldLabelGroup,
  FieldLabelRow,
  InputWrapper,
  TrailingActionButton,
} from './TokenInput.styles';
import { ActiveVariableToken, getActiveVariableToken } from './tokenize';

export interface TokenInputVariable {
  name: string;
  description?: string;
  /* Still recognized as a valid/known variable — renders as a normal (not "unsupported") chip —
   * but hidden from the autocomplete dropdown. For variables that can only ever arrive already
   * embedded in an incoming value (e.g. from a server-stored template), never something a user
   * should be able to type themselves.
   */
  selectable?: boolean;
}

export interface TokenInputProps {
  value: string;
  onChange?: (value: string) => void;
  variables: TokenInputVariable[];
  label?: string;
  helpText?: string | ReactNode;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  variablesListLabel?: string;
  // Floats inside the editor/suffix box, near its right edge (e.g. an "insert variable" button),
  // overlaid on the box itself, not the label above it.
  editorAdornment?: ReactNode;
  // A ghost suffix (e.g. a file extension) rendered as a `::after` on the editor itself, see
  // `Editor`'s styles, so it wraps together with the actual typed content instead of being a
  // separate, potentially misaligned element. Purely presentational, never part of the serialized
  // value; a plain string since it becomes a DOM attribute value, not real content.
  suffix?: string;
  // A plain clickable icon (e.g. a remove icon) rendered next to the editor row itself, outside
  // its border, but vertically centered with it specifically, not with the label above it.
  trailingAction?: ReactNode;
  onTrailingActionClick?: () => void;
  trailingActionLabel?: string;
}

export interface TokenInputHandle {
  // Inserts `{` at the current caret (or at the end of the content if the editor isn't focused)
  // and opens the variable autocomplete dropdown, the same code path typing `{` triggers.
  openVariablePicker: () => void;
}

// Matches a fully-closed `{{variableName}}` token (used to detect one just completed by hand,
// as opposed to `getActiveVariableToken`'s detection of a still-open `{query`).
const COMPLETED_VARIABLE_PATTERN = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;

// Extends the pure `ActiveVariableToken` (start/query within the active text node's own text)
// with a reference to that text node, so `insertVariable`/keyboard handlers know where in the DOM
// to splice the chip in.
interface DomActiveToken extends ActiveVariableToken {
  textNode: Text;
}

// A single-line text input whose value is a `{{variableName}}` template: known variables render
// as atomic, styled chips inline, deletable as a whole unit, with a `{`-triggered autocomplete
// dropdown for inserting new ones. See tokenDom.ts for why this is a contentEditable-based editor
// rather than a plain `<input>`.
const TokenInput = forwardRef<TokenInputHandle, TokenInputProps>(
  (
    {
      value,
      onChange,
      variables,
      label,
      helpText,
      placeholder,
      error,
      disabled,
      readOnly,
      variablesListLabel = 'Available variables',
      editorAdornment,
      suffix,
      trailingAction,
      onTrailingActionClick,
      trailingActionLabel,
    },
    ref,
  ) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const editorRowRef = useRef<HTMLDivElement>(null);
    const listboxId = useId();
    const editorId = useId();
    const highlightedOptionRef = useRef<HTMLDivElement>(null);
    const [activeToken, setActiveToken] = useState<DomActiveToken | null>(null);
    // Whether the current `activeToken`'s trigger `{` was inserted programmatically by
    // `openVariablePicker` (the `{ }` button), rather than typed by hand. If the user dismisses the
    // dropdown without picking anything, that synthetic `{` (and whatever they typed to filter it)
    // gets removed in `cancelActiveToken`. a hand-typed `{` is left alone, since it's real content
    // the user chose to type.
    const openedViaPickerRef = useRef(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const safeValue = value || '';
    const variableByName = useMemo(() => new Map(variables.map((variable) => [variable.name, variable])), [variables]);
    const isKnownVariable = (name: string) => variableByName.has(name);

    const selectableVariables = useMemo(
      () => variables.filter((variable) => variable.selectable !== false),
      [variables],
    );

    const filteredVariables = useMemo(() => {
      if (!activeToken) return [];
      const lowerCaseQuery = activeToken.query.toLowerCase();
      return selectableVariables.filter((variable) => variable.name.toLowerCase().startsWith(lowerCaseQuery));
    }, [activeToken, selectableVariables]);

    const isDropdownOpen = !readOnly && !disabled && !!activeToken && filteredVariables.length > 0;

    // Keep the keyboard-highlighted option visible when navigating a scrollable list.
    useEffect(() => {
      if (isDropdownOpen) highlightedOptionRef.current?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex, isDropdownOpen]);

    // The dropdown is portaled to `document.body` (see render below) so a scrollable ancestor
    // (e.g. the output-paths list) can never clip it, instead its position is computed here from
    // the field's own viewport-relative bounding box and kept in sync while open. Scroll listeners
    // are registered in the capture phase since scroll events don't bubble, but capturing on
    // `window` still observes scrolling on any ancestor between it and the field.
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

    useEffect(() => {
      if (!isDropdownOpen) {
        setDropdownPosition(null);
        return undefined;
      }

      const updatePosition = () => {
        const anchorEl = editorRowRef.current;
        if (!anchorEl) return;
        const rect = anchorEl.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isDropdownOpen]);

    // Variable chips' hover/focus tooltip is portaled to `document.body` for the same reason the
    // dropdown is, a chip commonly sits inside the scrollable output-paths list, which would
    // otherwise clip a tooltip trying to render above it. Tracked as the chip DOM element itself
    // (chips are built imperatively outside React, see tokenDom.ts) rather than React state driven
    // by a hover/focus flag on each chip.
    const [tooltipChip, setTooltipChip] = useState<HTMLElement | null>(null);

    const getTooltipChip = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof Element)) return null;
      const chip = target.closest('[data-var][data-tooltip]');
      return chip instanceof HTMLElement ? chip : null;
    };

    const handleChipMouseOver = (event: MouseEvent<HTMLDivElement>) => {
      const chip = getTooltipChip(event.target);
      if (chip) setTooltipChip(chip);
    };

    const handleChipMouseOut = (event: MouseEvent<HTMLDivElement>) => {
      const chip = getTooltipChip(event.target);
      if (!chip) return;
      // Keep it open while the chip still has keyboard focus, even if the mouse leaves.
      if (chip === document.activeElement) return;
      if (getTooltipChip(event.relatedTarget) === chip) return;
      setTooltipChip((current) => (current === chip ? null : current));
    };

    const handleChipFocus = (event: FocusEvent<HTMLDivElement>) => {
      const chip = getTooltipChip(event.target);
      if (chip) setTooltipChip(chip);
    };

    const handleChipBlur = (event: FocusEvent<HTMLDivElement>) => {
      const chip = getTooltipChip(event.target);
      if (!chip) return;
      // Keep it open while still hovered, even if keyboard focus moves elsewhere.
      if (chip.matches(':hover')) return;
      setTooltipChip((current) => (current === chip ? null : current));
    };

    const [tooltipPosition, setTooltipPosition] = useState<{
      top: number;
      left: number;
      placement: 'top' | 'bottom';
    } | null>(null);

    useEffect(() => {
      if (!tooltipChip) {
        setTooltipPosition(null);
        return undefined;
      }

      const updatePosition = () => {
        const rect = tooltipChip.getBoundingClientRect();
        // Enough headroom for a couple of lines of tooltip text, flips below the chip when there
        // isn't room above, rather than rendering off the top of the viewport.
        const placement: 'top' | 'bottom' = rect.top > 96 ? 'top' : 'bottom';
        setTooltipPosition({
          top: placement === 'top' ? rect.top - 8 : rect.bottom + 8,
          left: rect.left + rect.width / 2,
          placement,
        });
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [tooltipChip]);

    // Rebuilds the editor's DOM from `value` on mount and whenever an *external* value change is
    // detected (i.e. the serialized DOM no longer matches the incoming prop). Skipped while the
    // editor itself is focused so we never fight the user's caret mid-keystroke — a change made via
    // typing already gets pushed to `value` through `onChange`, so `serialize(editorEl) === value`
    // holds true in that case anyway.
    useEffect(() => {
      const editorEl = editorRef.current;
      if (!editorEl) return;
      if (document.activeElement === editorEl) return;
      if (serialize(editorEl) === safeValue) return;

      renderInto(editorEl, safeValue, isKnownVariable, (name) => variableByName.get(name)?.description);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeValue, variableByName]);

    const emitChange = () => {
      if (!editorRef.current) return;
      onChange?.(serialize(editorRef.current));
    };

    const createChip = (variableName: string): HTMLSpanElement =>
      createChipElement(variableName, isKnownVariable, (name) => variableByName.get(name)?.description);

    const syncActiveToken = () => {
      // Any real interaction that re-syncs the active token (typing, clicking) means what follows
      // is no longer the picker-button's synthetic `{` — `openVariablePicker` re-sets this to true
      // immediately after calling this function, for the one case where it should stay true.
      openedViaPickerRef.current = false;

      const selection = window.getSelection();
      const anchorNode = selection?.anchorNode;
      const anchorOffset = selection?.anchorOffset;

      if (
        !selection ||
        anchorOffset === undefined ||
        !anchorNode ||
        anchorNode.nodeType !== Node.TEXT_NODE ||
        !editorRef.current?.contains(anchorNode) ||
        isInsideChip(anchorNode)
      ) {
        setActiveToken(null);
        return;
      }

      const { text, caretPos } = stripCaretMarkers(anchorNode.textContent ?? '', anchorOffset);
      const token = getActiveVariableToken(text, caretPos);
      setActiveToken(token ? { ...token, textNode: anchorNode as Text } : null);
      setHighlightedIndex(0);
    };

    // Recognizes a `{{variableName}}` the user just finished typing by hand — closing brace and
    // all — without ever going through the dropdown, and converts it into a real chip immediately,
    // the same way `insertVariable`/paste do. Only fires when the just-typed closing `}}` sits right
    // at the caret, so it can't retroactively convert unrelated `{{...}}`-shaped text sitting
    // elsewhere in the same text node.
    const convertCompletedToken = () => {
      const selection = window.getSelection();
      const anchorNode = selection?.anchorNode;
      const anchorOffset = selection?.anchorOffset;

      if (
        !selection ||
        anchorOffset === undefined ||
        !anchorNode ||
        anchorNode.nodeType !== Node.TEXT_NODE ||
        !editorRef.current?.contains(anchorNode) ||
        isInsideChip(anchorNode)
      ) {
        return;
      }

      const textNode = anchorNode as Text;
      const parent = textNode.parentNode;
      if (!parent) return;

      const { text: cleanText, caretPos } = stripCaretMarkers(textNode.textContent ?? '', anchorOffset);

      COMPLETED_VARIABLE_PATTERN.lastIndex = 0;
      let match: RegExpExecArray | null;
      let completedMatch: RegExpExecArray | null = null;
      while ((match = COMPLETED_VARIABLE_PATTERN.exec(cleanText))) {
        if (match.index + match[0].length === caretPos) {
          completedMatch = match;
          break;
        }
      }
      if (!completedMatch) return;

      const variableName = completedMatch[1];
      const beforeText = cleanText.slice(0, completedMatch.index);
      const afterText = cleanText.slice(completedMatch.index + completedMatch[0].length);

      const chip = createChip(variableName);
      const afterNode = document.createTextNode(afterText);
      parent.replaceChild(afterNode, textNode);
      parent.insertBefore(chip, afterNode);
      parent.insertBefore(document.createTextNode(beforeText), chip);

      ensureCaretSlots(editorRef.current);

      const markerNode = chip.nextSibling as Text;
      const range = document.createRange();
      range.setStart(markerNode, 1);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);

      setActiveToken(null);
    };

    const handleInput = () => {
      convertCompletedToken();
      // Deleting all the way back to empty leaves a stray node behind (browsers auto-insert a
      // `<br>` into a contentEditable element to anchor the cursor), `serialize` correctly ignores
      // it, so the value is right, but the DOM is no longer truly `:empty`, so the CSS placeholder
      // never reappears. Force a real empty state whenever there's nothing left to preserve.
      if (editorRef.current && !editorRef.current.textContent) editorRef.current.innerHTML = '';
      emitChange();
      syncActiveToken();
    };

    // Inserts a literal `{` at the current caret (mirroring what typing `{` does) and reuses the
    // exact same `emitChange`/`syncActiveToken` pair `handleInput` calls to detect it and open the
    // dropdown. Falls back to inserting at the end of the content when the editor isn't focused (or
    // the caret isn't usably placed inside it) rather than fighting for a caret position that isn't
    // there.
    const openVariablePicker = () => {
      const editorEl = editorRef.current;
      if (!editorEl || readOnly || disabled) return;

      const wasFocused = document.activeElement === editorEl;
      const selection = window.getSelection();

      let range: Range;
      if (
        wasFocused &&
        selection &&
        selection.rangeCount > 0 &&
        editorEl.contains(selection.anchorNode) &&
        !isInsideChip(selection.anchorNode)
      ) {
        range = selection.getRangeAt(0);
      } else {
        range = document.createRange();
        range.selectNodeContents(editorEl);
        range.collapse(false);
      }

      range.deleteContents();
      const braceNode = document.createTextNode('{');
      range.insertNode(braceNode);

      const caretRange = document.createRange();
      caretRange.setStart(braceNode, 1);
      caretRange.collapse(true);

      editorEl.focus();
      selection?.removeAllRanges();
      selection?.addRange(caretRange);

      emitChange();
      syncActiveToken();
      openedViaPickerRef.current = true;
    };

    useImperativeHandle(ref, () => ({ openVariablePicker }));

    // `Editor` fills `EditorBox`, but its own padding/border can still leave a sliver of "dead"
    // clickable space at the box's edges that isn't part of the contentEditable element itself.
    // Clicking there should behave like clicking past the end of text in a normal `<input>`, focus
    // the field and place the caret at the end, rather than doing nothing.
    const focusEditorAtEnd = (event: MouseEvent<HTMLDivElement>) => {
      if (readOnly || disabled) return;
      const target = event.target as HTMLElement;
      if (editorRef.current?.contains(target) || target.closest('button')) return;

      event.preventDefault();
      const editorEl = editorRef.current;
      if (!editorEl) return;

      editorEl.focus();
      const range = document.createRange();
      range.selectNodeContents(editorEl);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    };

    // Dismisses the dropdown (Escape, or blur). If it was opened via the `{ }` button and nothing
    // was selected, also removes the synthetic `{` (and whatever query text followed it) it
    // inserted, otherwise a stray, unused `{` is left behind in the field. A hand-typed `{` is
    // left untouched, since it's real content the user chose to type.
    const cancelActiveToken = () => {
      if (activeToken && openedViaPickerRef.current) {
        const { textNode, start, query } = activeToken;
        const { text: cleanText } = stripCaretMarkers(textNode.textContent ?? '', 0);
        const removeLength = 1 + query.length;
        textNode.textContent = cleanText.slice(0, start) + cleanText.slice(start + removeLength);
        openedViaPickerRef.current = false;
        emitChange();
      }
      setActiveToken(null);
    };

    const insertVariable = (variableName: string) => {
      if (!activeToken || !editorRef.current) return;

      const { textNode, start, query } = activeToken;
      const parent = textNode.parentNode;
      if (!parent) return;

      // `start`/`query` were computed against the marker-stripped text (see `syncActiveToken`), so
      // clean this node's text the same way before slicing — the fresh caret markers `ensureCaretSlots`
      // adds below will re-establish proper marker placement around the new chip.
      const { text: cleanText } = stripCaretMarkers(textNode.textContent ?? '', 0);

      // The dropdown triggers on the trigger `{` closest to the caret (e.g. typing `{{` opens it
      // after the second brace), but any extra `{` typed just before it (from `{{`, `{{{`, ...) is
      // never part of the inserted `{{variable}}` token and must be swallowed too, or it's left
      // behind as a stray brace (`{{{variable}}}`).
      let tokenStart = start;
      while (tokenStart > 0 && cleanText[tokenStart - 1] === '{') tokenStart -= 1;

      const beforeText = cleanText.slice(0, tokenStart);
      const afterText = cleanText.slice(start + 1 + query.length);

      const chip = createChip(variableName);
      const afterNode = document.createTextNode(afterText);
      parent.replaceChild(afterNode, textNode);
      parent.insertBefore(chip, afterNode);
      parent.insertBefore(document.createTextNode(beforeText), chip);

      ensureCaretSlots(editorRef.current);

      // `ensureCaretSlots` guarantees a caret-marker text node right after the chip — that's where
      // the caret belongs, right past the chip and before any text that followed it.
      const markerNode = chip.nextSibling as Text;
      const range = document.createRange();
      range.setStart(markerNode, 1);
      range.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      setActiveToken(null);
      editorRef.current.focus();
      emitChange();
    };

    // Browsers default to inserting the clipboard's full rich HTML on paste into a contentEditable
    // region — real elements, not just text. Beyond the visual mess, `serialize` trusts *any*
    // `[data-var]` element as a legitimate chip, so a pasted one could forge a variable (e.g. the
    // BE-only `sourceFilenameBase`) that the dropdown deliberately never offers. Force plain text
    // only: read it from the clipboard ourselves, then run it through the same segment-building
    // logic `renderInto` uses so any recognized `{{variable}}` in the pasted text becomes a real
    // chip instead of staying literal text — matching how an externally-provided value renders.
    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();

      const text = event.clipboardData.getData('text/plain').replace(/[\r\n]+/g, '');
      if (!text) return;

      const selection = window.getSelection();
      if (!selection?.rangeCount || !editorRef.current) return;

      const range = selection.getRangeAt(0);
      if (!editorRef.current.contains(range.commonAncestorContainer) || isInsideChip(range.startContainer)) return;

      range.deleteContents();

      const nodes = buildSegmentNodes(text, isKnownVariable, (name) => variableByName.get(name)?.description);
      const lastNode = nodes[nodes.length - 1];
      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => fragment.appendChild(node));
      range.insertNode(fragment);

      ensureCaretSlots(editorRef.current);

      // Place the caret right after whatever we just pasted: inside the caret-marker `ensureCaretSlots`
      // guarantees after a trailing chip, or at the end of a trailing literal text node.
      const caretRange = document.createRange();
      if (isChipElement(lastNode)) {
        caretRange.setStart(lastNode.nextSibling as Text, 1);
      } else {
        caretRange.setStart(lastNode, (lastNode.textContent ?? '').length);
      }
      caretRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caretRange);

      emitChange();
      syncActiveToken();
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
      // Dropdown navigation/selection keys are fully handled in `handleKeyDown` (including
      // `highlightedIndex`); re-running `syncActiveToken` here would reset it back to 0 on every
      // ArrowUp/ArrowDown press since it always sets `highlightedIndex` from the caret position.
      if (isDropdownOpen && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) return;
      syncActiveToken();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (isDropdownOpen) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setHighlightedIndex((previousIndex) => (previousIndex + 1) % filteredVariables.length);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setHighlightedIndex(
            (previousIndex) => (previousIndex - 1 + filteredVariables.length) % filteredVariables.length,
          );
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          insertVariable(filteredVariables[highlightedIndex].name);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          cancelActiveToken();
          return;
        }
      }

      // The template is single-line; never let contentEditable insert a line break.
      if (event.key === 'Enter') {
        event.preventDefault();
        return;
      }

      if (
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        handlePlainArrowAroundChipBoundary(event.key, window.getSelection(), editorRef.current)
      ) {
        event.preventDefault();
        return;
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        // Select the atomic chip for the browser to delete. Do not prevent its native editing
        // operation: that retains the browser's undo/redo history for whole-chip deletion.
        selectChipForNativeDeletion(
          event.key === 'Backspace' ? 'backward' : 'forward',
          window.getSelection(),
          editorRef.current,
        );
      }
    };

    return (
      <FieldContainer>
        <InputWrapper>
          {(label || helpText) && (
            <FieldLabelRow>
              <FieldLabelGroup>
                {label && <FieldLabel htmlFor={editorId}>{label}</FieldLabel>}
                {helpText && <HelpTooltip $helpText={helpText} />}
              </FieldLabelGroup>
            </FieldLabelRow>
          )}
          <EditorRow ref={editorRowRef}>
            <EditorBox $hasError={!!error} $isReadOnly={!!readOnly} onMouseDown={focusEditorAtEnd}>
              <Editor
                id={editorId}
                ref={editorRef}
                contentEditable={!readOnly && !disabled}
                suppressContentEditableWarning
                role="combobox"
                aria-expanded={isDropdownOpen}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={isDropdownOpen ? `${listboxId}-option-${highlightedIndex}` : undefined}
                aria-readonly={readOnly}
                aria-disabled={disabled}
                data-placeholder={readOnly ? undefined : placeholder}
                data-extension={suffix || undefined}
                $hasError={!!error}
                $isReadOnly={!!readOnly}
                $hasEditorAdornment={!!editorAdornment}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onPaste={handlePaste}
                onMouseUp={syncActiveToken}
                onMouseOver={handleChipMouseOver}
                onMouseOut={handleChipMouseOut}
                onFocus={handleChipFocus}
                onBlur={(event) => {
                  cancelActiveToken();
                  handleChipBlur(event);
                }}
              />
              {editorAdornment && <EditorAdornmentSlot>{editorAdornment}</EditorAdornmentSlot>}
            </EditorBox>
            {trailingAction && (
              <TrailingActionButton type="button" onClick={onTrailingActionClick} aria-label={trailingActionLabel}>
                {trailingAction}
              </TrailingActionButton>
            )}
          </EditorRow>

          {isDropdownOpen &&
            dropdownPosition &&
            createPortal(
              <DropdownCard
                id={listboxId}
                role="listbox"
                aria-label={variablesListLabel}
                onMouseDown={(event) => event.preventDefault()}
                style={{
                  position: 'fixed',
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width,
                }}
              >
                {filteredVariables.map((variable, index) => (
                  <DropdownOption
                    key={variable.name}
                    ref={index === highlightedIndex ? highlightedOptionRef : undefined}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    $isActive={index === highlightedIndex}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => insertVariable(variable.name)}
                  >
                    <DropdownOptionName>{`{{${variable.name}}}`}</DropdownOptionName>
                    {variable.description && <DropdownOptionHelper>{variable.description}</DropdownOptionHelper>}
                  </DropdownOption>
                ))}
              </DropdownCard>,
              document.body,
            )}

          {tooltipChip &&
            tooltipPosition &&
            createPortal(
              <ChipTooltip
                role="tooltip"
                $placement={tooltipPosition.placement}
                style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
              >
                {tooltipChip.dataset.tooltip}
              </ChipTooltip>,
              document.body,
            )}
        </InputWrapper>

        {error && <ErrorText>{error}</ErrorText>}
      </FieldContainer>
    );
  },
);

export default TokenInput;
